"use client";

import { useState } from "react";
import { Tag } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { useAuth } from "@/context/auth-context";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useOrderTotals } from "@/hooks/use-order-totals";
import { useCaliiPlusMembership } from "@/hooks/use-calii-plus-membership";
import { getCaliiPlusMessage } from "@/lib/calii-plus";
import CaliiPlusModalContent from "./calii-plus-modal-content";
import CaliiPlusMemberModalContent from "./calii-plus-member-modal-content";
import { cn } from "@/lib/utils";

export function CaliiPlusMemberBanner({ className }: { className?: string }) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn("w-full cursor-pointer bg-[#232428] text-[#E3C425] flex items-center justify-start gap-2 px-5 py-3 -ml-[1px]", className)}
      >
        <Tag className="h-5 w-5 flex-shrink-0 mr-2" />
        <div className="flex flex-col items-start">
          <span className="font-semibold text-sm">Tienes $1000 de regalo al mes y envíos gratis con Calii+</span>
          <span className="text-xs text-white/90">Ver detalles</span>
        </div>
      </button>

      {isDesktop ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0 [&>button:last-child]:hidden flex flex-col max-h-[90svh]">
            <DialogTitle className="sr-only">Calii+</DialogTitle>
            <DialogDescription className="sr-only">Tus beneficios Calii+</DialogDescription>
            <CaliiPlusMemberModalContent onClose={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent className="p-0 min-h-[90svh]">
            <DrawerTitle className="sr-only">Calii+</DrawerTitle>
            <DrawerDescription className="sr-only">Tus beneficios Calii+</DrawerDescription>
            <CaliiPlusMemberModalContent onClose={() => setOpen(false)} />
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
}

type Props = {
  titleClass?: string;
  className?: string;
};

export default function CaliiPlusCheckbox({ titleClass = "", className = "" }: Props) {
  const { user } = useAuth();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [open, setOpen] = useState(false);
  const { freeTrialMonths } = useCaliiPlusMembership();
  const orderTotals = useOrderTotals();
  const message = getCaliiPlusMessage(orderTotals.subtotal, freeTrialMonths);

  if (user?.is_calii_plus_member) return null;

  return (
    <div className={className}>
      <button
        type="button"
        className="flex items-center gap-3 w-full text-left cursor-pointer"
        onClick={() => setOpen(true)}
      >
        <Checkbox
          checked={false}
          className="pointer-events-none h-6 w-6 data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500"
        />
        <div>
          <span className={cn("text-base font-medium", titleClass)}>{message.title}</span>
          {message.subtitle && (
            <p className="text-xs text-muted-foreground mt-0.5">{message.subtitle}</p>
          )}
        </div>
      </button>

      {isDesktop ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0 [&>button:last-child]:hidden flex flex-col max-h-[90svh]">
            <DialogTitle className="sr-only">Calii+</DialogTitle>
            <DialogDescription className="sr-only">Membresía con beneficios exclusivos</DialogDescription>
            <CaliiPlusModalContent onClose={() => setOpen(false)} freeTrialMonths={freeTrialMonths} signupSource="checkbox_cart_screen_web" />
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent className="p-0 min-h-[75svh]">
            <DrawerTitle className="sr-only">Calii+</DrawerTitle>
            <DrawerDescription className="sr-only">Membresía con beneficios exclusivos</DrawerDescription>
            <CaliiPlusModalContent onClose={() => setOpen(false)} freeTrialMonths={freeTrialMonths} signupSource="checkbox_cart_screen_web" />
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
}
